/**
 * Contracts feature API layer — OpenAPI-first (§9.1, §9.3).
 *
 * Admin contracts CRUD + contract-by-meter lookup.
 */

import {
  getAdminContractsListQueryKey,
  useAdminContractCreate as useGenCreate,
  useAdminContractDelete as useGenDelete,
  useAdminContractGet as useGenGet,
  useAdminContractsList as useGenList,
  useAdminContractUpdate as useGenUpdate,
} from "@shared/api/generated/admin/admin";
import { useDeviceContractByMeter as useGenContractByMeter } from "@shared/api/generated/devices/devices";
import type { AdminContractsListParams } from "@shared/api/generated/model";
import { useQueryClient } from "@tanstack/react-query";

export type {
  Contract,
  ContractCreateRequest,
  ContractCreateResponse,
  ContractMeterLink,
  ContractUpdateRequest,
} from "@shared/api/generated/model";

// ── Filter interface ─────────────────────────────────────────────────────────

export interface ContractFilters {
  inn?: string;
  contract_number?: string;
  company_name?: string;
}

// ── Queries ─────────────────────────────────────────────────────────────────

export const useAdminContracts = (filters?: ContractFilters, enabled = true) => {
  const params: AdminContractsListParams = {
    ...(filters?.inn && { inn: filters.inn }),
    ...(filters?.contract_number && { contract_number: filters.contract_number }),
    ...(filters?.company_name && { company_name: filters.company_name }),
  };
  return useGenList(params, { query: { enabled, staleTime: 60_000 } });
};

export const useAdminContract = (id?: string) => {
  const enabled = Boolean(id && id !== "new");
  return useGenGet(id ?? "", { query: { enabled, staleTime: 60_000 } });
};

export const useContractByMeter = (
  regionId?: number,
  objectId?: number,
  feederId?: number,
  meterNumber?: number | string,
) => {
  const enabled = Boolean(regionId && objectId && feederId && meterNumber);
  return useGenContractByMeter(
    regionId ?? 0,
    objectId ?? 0,
    feederId ?? 0,
    meterNumber != null ? String(meterNumber) : "",
    { query: { enabled, staleTime: 60_000 } },
  );
};

// ── Mutations ───────────────────────────────────────────────────────────────

export const useCreateAdminContract = () => {
  const qc = useQueryClient();
  return useGenCreate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminContractsListQueryKey() });
      },
    },
  });
};

export const useUpdateAdminContract = () => {
  const qc = useQueryClient();
  return useGenUpdate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminContractsListQueryKey() });
      },
    },
  });
};

export const useDeleteAdminContract = () => {
  const qc = useQueryClient();
  return useGenDelete({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminContractsListQueryKey() });
      },
    },
  });
};
